import { useEffect, useRef, useState } from "react";
import { CloudOff, Dumbbell, Plus, RotateCw, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { ActionButton, Field } from "./kit";
import CoachDrillAssignSheet from "./CoachDrillAssignSheet";
import { SavedCard } from "./GoalNotice";
import { createDrill, fetchAssignedDrills, fetchDrillHolders, fetchMyDrills, updateDrill } from "../lib/drills";
import { holdersLine, inRosterOrder } from "../lib/drillHolders";

/**
 * The coach's Drill bank (#509, redrawn in #1159 slice 2: board CoachDrillBank,
 * round 12, D298, D251). A coach writes a drill once, in their own words, and
 * assigns it to one player or several from here (`CoachDrillAssignSheet`).
 *
 * Mounted only in LearningHub's coach `drills` section (new look). The old look
 * and admin keep the shipped `CoachDrillBankPanel` on `LearningHubLegacy` (D217),
 * so this is a separate component: nothing here may leak onto that page, and the
 * fan-out below (`GET /players` is every player for an admin) never runs there.
 * DRILLS ONLY: reviewed guides are the #98 pipeline.
 *
 * THE FETCHES ARE NOT SWALLOWED (#511, the #271 class). The bank is "loaded"
 * only when the drills AND every linked player's assignments have arrived
 * (S4, D301: the fan-out in `fetchDrillHolders`). One failed read is the
 * failed card with Try again, never a wrong "Not assigned yet" (an
 * orchestrator ruling, open to veto).
 *
 * Bounds mirror the API's (title <= 120, body <= 4000); `maxLength` is a
 * courtesy, the server is the gate. A failed create or edit keeps the
 * shipped toast words (no board draws them).
 */
const TITLE_MAX = 120;
const BODY_MAX = 4000;

const CARD = { background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" };
const STACK = { display: "flex", flexDirection: "column", gap: "var(--nw-space-stack)" };
const TEXTAREA = {
  width: "100%", boxSizing: "border-box", padding: 12, background: "var(--nw-surface)", border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-control)", fontSize: 15, lineHeight: 1.45, color: "var(--nw-ink)", fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
};
const SMALL_CONTROL = {
  display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", minHeight: 44, padding: "0 14px",
  background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)",
  fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", boxShadow: "none", cursor: "pointer",
};
// The navy Save in an edit. Literal (= --nw-navy, #0B2E59) so a unit test can read it: jsdom drops a `var()` background.
const NAVY_BUTTON = { background: "#0B2E59", color: "#fff" };

function BodyField({ id, value, onChange, placeholder, rows, testId }) {
  return (
    <div>
      <label htmlFor={id} style={{ display: "block", marginBottom: 4, fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>The drill</label>
      <textarea
        id={id}
        className="nw-kit-input"
        value={value}
        onChange={onChange}
        maxLength={BODY_MAX}
        rows={rows}
        placeholder={placeholder}
        data-testid={testId}
        style={{ ...TEXTAREA, minHeight: 90 }}
      />
    </div>
  );
}

function Loading() {
  return (
    <div data-testid="drill-bank-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 84, boxSizing: "border-box" }}>
          <div style={{ width: "50%", height: 12, borderRadius: 6, background: "var(--nw-line)" }} />
          <div style={{ width: "30%", height: 10, borderRadius: 5, background: "var(--nw-line-faint)" }} />
        </div>
      ))}
    </div>
  );
}

function Failed({ onRetry }) {
  return (
    <div data-testid="drill-bank-error" style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <CloudOff size={20} aria-hidden="true" style={{ color: "var(--nw-alert)", flexShrink: 0 }} />
        <div className="nw-body-strong">Couldn't load your drills</div>
      </div>
      <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Nothing has been lost — we just couldn't reach the server.</div>
      <button type="button" data-testid="drill-bank-error-retry" onClick={onRetry} style={SMALL_CONTROL}>
        <RotateCw size={14} aria-hidden="true" />
        Try again
      </button>
    </div>
  );
}

function Empty() {
  return (
    <div data-testid="drill-bank-empty" style={{ ...CARD, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center" }}>
      <div aria-hidden="true" style={{ width: 52, height: 52, borderRadius: 16, background: "var(--nw-navy-tint)", color: "var(--nw-navy)", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Dumbbell size={22} aria-hidden="true" style={{ color: "var(--nw-navy)" }} />
      </div>
      <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>No drills yet.</div>
      <div className="nw-lede">Write one above. It stays in this bank, ready to assign to any of your players.</div>
    </div>
  );
}

export default function CoachDrillBankSection() {
  const [drills, setDrills] = useState([]);
  const [roster, setRoster] = useState([]);
  // drill_id -> [{ player_id, full_name, assignment_id }], in roster order.
  const [holders, setHolders] = useState({});
  // "loading" | "loaded" | "failed": "empty" may only be claimed from "loaded" (#271/#511).
  const [bankStatus, setBankStatus] = useState("loading");
  const [retryNonce, setRetryNonce] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  // { drillId, title, body } while an inline edit is open, null otherwise.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  // Per-player write counter (review F2): bumped by every assign and withdraw, so a
  // re-read that was sent before a later write is dropped when it answers.
  const writes = useRef({});
  const [assignFor, setAssignFor] = useState(null);
  // The line that stays after an assign (D48).
  const [assignedLine, setAssignedLine] = useState("");

  useEffect(() => {
    let cancelled = false;
    setBankStatus("loading");
    Promise.all([fetchMyDrills(), fetchDrillHolders()])
      .then(([rows, found]) => {
        if (cancelled) return;
        setDrills(rows);
        setRoster(found.roster);
        setHolders(found.holders);
        setBankStatus("loaded");
      })
      .catch(() => {
        if (!cancelled) setBankStatus("failed");
      });
    return () => { cancelled = true; };
  }, [retryNonce]);

  const create = async () => {
    if (!title.trim() || !body.trim() || busy) return;
    setBusy(true);
    try {
      const drill = await createDrill(title.trim(), body.trim());
      // Newest first, matching the server's own order (listDrillsForCoach).
      setDrills((prev) => [drill, ...prev]);
      setTitle("");
      setBody("");
    } catch {
      toast.error("Could not save that drill. Check the title and text and try again.");
    }
    setBusy(false);
  };

  const saveEdit = async () => {
    if (!editing || !editing.title.trim() || !editing.body.trim() || saving) return;
    setSaving(true);
    try {
      const updated = await updateDrill(editing.drillId, editing.title.trim(), editing.body.trim());
      setDrills((prev) => prev.map((d) => (d.drill_id === updated.drill_id ? updated : d)));
      setEditing(null);
    } catch {
      toast.error("Could not save the edit. Check the title and text and try again.");
    }
    setSaving(false);
  };

  const bump = (playerId) => {
    writes.current[playerId] = (writes.current[playerId] || 0) + 1;
    return writes.current[playerId];
  };

  const addHolders = (drillId, added) => {
    added.forEach((h) => bump(h.player_id));
    setHolders((prev) => ({ ...prev, [drillId]: inRosterOrder([...(prev[drillId] || []), ...added], roster) }));
  };

  // The DELETE succeeded: drop the row now, then re-read that player's
  // assignments so the holders stay what the server says (#1159: "refresh that
  // player's holders"). If the re-read fails the local drop stands.
  const withdrawn = async (playerId, assignmentId) => {
    const stamp = bump(playerId);
    setHolders((prev) => {
      const next = {};
      for (const [drillId, list] of Object.entries(prev)) next[drillId] = list.filter((h) => h.assignment_id !== assignmentId);
      return next;
    });
    try {
      const fresh = await fetchAssignedDrills(playerId);
      // A later assign or withdraw for this player made this answer stale: drop it.
      if (writes.current[playerId] !== stamp) return;
      const player = roster.find((p) => p.player_id === playerId);
      setHolders((prev) => {
        const next = {};
        for (const [drillId, list] of Object.entries(prev)) next[drillId] = list.filter((h) => h.player_id !== playerId);
        for (const a of fresh) {
          next[a.drill_id] = inRosterOrder(
            [...(next[a.drill_id] || []), { player_id: playerId, full_name: player?.full_name || "", assignment_id: a.assignment_id }],
            roster,
          );
        }
        return next;
      });
    } catch {
      // keep the local drop
    }
  };

  const openAssign = (drill) => {
    setAssignedLine("");
    setAssignFor(drill.drill_id);
  };

  const query = filter.trim().toLowerCase();
  const visible = query ? drills.filter((d) => d.title.toLowerCase().includes(query)) : drills;
  const assignDrillRow = drills.find((d) => d.drill_id === assignFor) || null;

  return (
    <div style={STACK} data-testid="drill-bank-panel">
      <div style={{ ...CARD, display: "flex", flexDirection: "column", gap: 10, padding: 14 }}>
        <div style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: 16, color: "var(--nw-ink)" }}>New drill</div>
        <Field
          label="Drill title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={TITLE_MAX}
          placeholder="e.g. Front-foot drive shadow reps"
          data-testid="drill-bank-title"
        />
        <BodyField
          id="drill-bank-body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={4}
          placeholder="The drill itself: setup, reps, what to focus on."
          testId="drill-bank-body"
        />
        <ActionButton
          onClick={create}
          disabled={!title.trim() || !body.trim() || busy}
          icon={<Plus size={18} strokeWidth={2} />}
          data-testid="drill-bank-create"
        >
          {busy ? "Saving…" : "Create a drill"}
        </ActionButton>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nw-space-grid)" }} data-testid="drill-bank-list">
        <div className="nw-section" style={{ fontSize: 11 }} data-testid="drill-bank-count">
          {bankStatus === "loaded" ? `Your drills · ${drills.length}` : "Your drills"}
        </div>

        {assignedLine && <SavedCard data-testid="drill-bank-assigned">{assignedLine}</SavedCard>}

        {bankStatus === "loading" && <Loading />}
        {bankStatus === "failed" && <Failed onRetry={() => setRetryNonce((n) => n + 1)} />}
        {bankStatus === "loaded" && drills.length === 0 && <Empty />}

        {bankStatus === "loaded" && drills.length > 0 && (
          <label
            style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 46, padding: "0 12px", ...CARD, borderRadius: "var(--nw-radius-control)" }}
          >
            <Search size={18} aria-hidden="true" style={{ color: "var(--nw-ink-muted)", flexShrink: 0 }} />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find a drill by title…"
              aria-label="Find a drill by title"
              data-testid="drill-bank-filter"
              style={{ flex: 1, minWidth: 0, border: 0, outline: "none", background: "none", fontSize: 15, color: "var(--nw-ink)", fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}
            />
          </label>
        )}

        {bankStatus === "loaded" && drills.length > 0 && visible.length === 0 && (
          <div className="nw-help" data-testid="drill-bank-filter-empty">{`No drill title matches “${filter.trim()}”.`}</div>
        )}

        {bankStatus === "loaded" && visible.map((d) => {
          const isEditing = editing?.drillId === d.drill_id;
          const held = holders[d.drill_id] || [];
          return (
            <div key={d.drill_id} style={{ ...CARD, display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px" }} data-testid={`drill-bank-drill-${d.drill_id}`}>
              {isEditing ? (
                <>
                  <Field
                    label="Drill title"
                    value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    maxLength={TITLE_MAX}
                    data-testid="drill-bank-edit-title"
                  />
                  <BodyField
                    id={`drill-bank-edit-body-${d.drill_id}`}
                    value={editing.body}
                    onChange={(e) => setEditing({ ...editing, body: e.target.value })}
                    rows={3}
                    testId="drill-bank-edit-body"
                  />
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" style={SMALL_CONTROL} onClick={() => setEditing(null)} data-testid="drill-bank-edit-cancel">Cancel</button>
                    <button
                      type="button"
                      style={{ ...SMALL_CONTROL, ...NAVY_BUTTON, border: 0, padding: "0 16px", opacity: !editing.title.trim() || !editing.body.trim() ? 0.6 : 1 }}
                      onClick={saveEdit}
                      disabled={!editing.title.trim() || !editing.body.trim() || saving}
                      data-testid="drill-bank-edit-save"
                    >
                      {saving ? "Saving…" : "Save"}
                    </button>
                  </div>
                  {/* D343 (D259): the board draws the line below Cancel and Save. */}
                  <div className="nw-help" data-testid="drill-bank-edit-note">Players who have this drill see the new version.</div>
                </>
              ) : (
                <>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                    <span aria-hidden="true" style={{ width: 32, height: 32, flexShrink: 0, borderRadius: 10, background: "var(--nw-navy-tint)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                      <Dumbbell size={16} style={{ color: "var(--nw-navy)" }} />
                    </span>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0, flexGrow: 1 }}>
                      <span style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink)", overflowWrap: "anywhere" }}>{d.title}</span>
                      <span
                        style={{ fontSize: 13, lineHeight: 1.45, color: "var(--nw-ink-lede)", whiteSpace: "pre-wrap", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", overflowWrap: "anywhere" }}
                      >
                        {d.body}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--nw-ink-muted)" }} data-testid={`drill-bank-holders-${d.drill_id}`}>{holdersLine(held)}</span>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button type="button" style={SMALL_CONTROL} onClick={() => openAssign(d)} aria-label={`Assign ${d.title}`} data-testid={`drill-bank-assign-${d.drill_id}`}>
                      <Send size={16} aria-hidden="true" />
                      Assign
                    </button>
                    <button
                      type="button"
                      style={SMALL_CONTROL}
                      onClick={() => setEditing({ drillId: d.drill_id, title: d.title, body: d.body })}
                      aria-label={`Edit ${d.title}`}
                      data-testid={`drill-bank-edit-${d.drill_id}`}
                    >
                      Edit
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      {assignDrillRow && (
        <CoachDrillAssignSheet
          key={assignDrillRow.drill_id}
          drill={assignDrillRow}
          roster={roster}
          holders={holders[assignDrillRow.drill_id] || []}
          onClose={() => setAssignFor(null)}
          onAdd={addHolders}
          onWithdrawn={withdrawn}
          onAssigned={setAssignedLine}
        />
      )}
    </div>
  );
}
